import { action, computed, observable, makeObservable } from "mobx";
import type { RefObject } from "react";
import { getBoundingClientRect } from "../../util/functions/getBoundingClientRect";
import { type IPointInWindow, PointInWindow } from "../../viewer/rendering/Point";
import type { ISizeInWindow } from "../../viewer/rendering/Size";

export interface IRichToolTipShowOptions {
    size?: ISizeInWindow;
    maxSize?: ISizeInWindow;
    minSize?: ISizeInWindow;
    zIndex?: number;
    className?: string;
}

export type RichToolTipPosition = "top" | "right" | "bottom" | "left" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
export type DangerousHtml = { __html: string };

export function isDangerousHtml(data: any): data is DangerousHtml {
    return data.__html !== undefined;
}

export type RichToolTipContentType = JSX.Element | string | JSX.Element[] | DangerousHtml | (() => (JSX.Element | string | JSX.Element[] | DangerousHtml));

export interface IRichToolTipStore {
    showToolTip(content: RichToolTipContentType, position: RichToolTipPosition, container: RefObject<HTMLElement>, options?: IRichToolTipShowOptions, delay?: number, margin?: number): void;
    hideToolTip(): void;
    updateLocation(location: IPointInWindow): void;
    toolTipComponentDidUpdate(domRect: DOMRect): void;

    readonly isVisible: boolean;

    readonly richToolTipContent: RichToolTipContentType | null;
    readonly richToolTipLocation: IPointInWindow | null;
    readonly richToolTipShowOptions: IRichToolTipShowOptions | null;
}

export class RichToolTipStore implements IRichToolTipStore {
    private readonly _renderingPosition: number = -1000;
    private _timeoutOnMouseOver: any = null;
    private readonly _default_delay: number = 100; //in ms
    private readonly _default_margin: number = 3; //in px

    @observable
    private _richToolTipLocation: IPointInWindow | null = null;

    @observable
    private _richToolTipContent: RichToolTipContentType | null = null;

    @observable
    private _richToolTipShowOptions: IRichToolTipShowOptions | null = null;

    @observable
    private _richToolTipMargin: number = this._default_margin;

    private _containerDivBoundingRect: DOMRect | null = null;

    @computed
    private get monitorWindowHeight(): number {
        return window.innerHeight;
    }

    @computed
    private get monitorWindowWidth(): number {
        return window.innerWidth;
    }

    @computed
    public get isVisible(): boolean {
        return this._richToolTipContent !== null && this._richToolTipLocation !== null;
    }

    constructor() {
        makeObservable<
            RichToolTipStore,
            "_richToolTipLocation" | "_richToolTipContent" | "_richToolTipShowOptions" | "_richToolTipMargin" | "monitorWindowHeight" | "monitorWindowWidth"
        >(this);
    }

    public showToolTip = (
        content: RichToolTipContentType,
        position: RichToolTipPosition,
        container: RefObject<HTMLElement>,
        options?: IRichToolTipShowOptions,
        delay?: number,
        margin?: number,
    ): void => {
        this._timeoutOnMouseOver = setTimeout(() => this.innerShowToolTip(content, position, container, options, margin), delay ?? this._default_delay);
    };

    @action
    private readonly innerShowToolTip = (
        content: RichToolTipContentType,
        position: RichToolTipPosition,
        container: RefObject<HTMLElement>,
        options?: IRichToolTipShowOptions,
        margin?: number,
    ): void => {
        if (!container.current)
            return;
        
        this._richToolTipContent = content;
        this._richToolTipShowOptions = options ?? null;
        this._richToolTipMargin = margin ?? this._default_margin;
        this._containerDivBoundingRect = getBoundingClientRect(container.current);

        this.setPosition(position);
        this.updateLocation(new PointInWindow(this._renderingPosition, this._renderingPosition));
        console.debug(`📌 showing a rich tooltip at render location ${this._richToolTipLocation}`);
    };

    @action
    public hideToolTip = () => {
        this._richToolTipLocation = null;
        this._richToolTipContent = null;
        if (this._timeoutOnMouseOver) {
            clearTimeout(this._timeoutOnMouseOver);
        }
        console.debug("📌 hiding a rich tooltip");
    };

    @observable
    private _position: RichToolTipPosition = "top";

    @action
    private setPosition(value: RichToolTipPosition): void {
        this._position = value;
    }

    @action
    public updateLocation = (location: IPointInWindow) => {
        this._richToolTipLocation = location;
    };

    @computed
    public get richToolTipContent(): RichToolTipContentType | null {
        return this._richToolTipContent;
    }

    @computed
    public get richToolTipLocation(): IPointInWindow | null {
        return this._richToolTipLocation;
    }

    @computed
    public get richToolTipShowOptions(): IRichToolTipShowOptions | null {
        return this._richToolTipShowOptions;
    }

    @action
    public toolTipComponentDidUpdate(domRectTooltip: DOMRect) {
        //Already rendered on correct position
        if (this._richToolTipLocation && this._richToolTipLocation.x !== this._renderingPosition) return;

        const { _position } = this;
        const { width: tooltipWidth, height: tooltipHeight } = domRectTooltip;

        const calculatedRichToolTipLocation: PointInWindow = this.calculatePointInWindowBasedOnPosition(_position, tooltipWidth, tooltipHeight);
        const adjustedRichToolTipLocation: PointInWindow = this.checkOverflowAndAdjustIfNeeded(calculatedRichToolTipLocation, tooltipWidth, tooltipHeight);

        this.updateLocation(adjustedRichToolTipLocation);
        console.debug(`📌 showing a rich tooltip at calculated location ${this._richToolTipLocation}`);
    }

    private calculatePointInWindowBasedOnPosition(position: RichToolTipPosition, tooltipWidth: number, tooltipHeight: number): PointInWindow {
        const { _richToolTipMargin, _containerDivBoundingRect } = this;
        if (!_containerDivBoundingRect) {
            throw new Error("ContainerDivBoundingRect is not yet defined; however we're already trying to calculate the tooltip's location based on the position");
        }
        const { x: containerX, y: containerY, width: containerWidth, height: containerHeight } = _containerDivBoundingRect;
        switch (position) {
            case "top":
                return new PointInWindow(
                    containerX + containerWidth / 2 - tooltipWidth / 2,
                    containerY - tooltipHeight - _richToolTipMargin
                );
            case "right":
                return new PointInWindow(
                    containerX + containerWidth + _richToolTipMargin,
                    containerY + containerHeight / 2 - tooltipHeight / 2
                );
            case "bottom":
                return new PointInWindow(
                    containerX + containerWidth / 2 - tooltipWidth / 2,
                    containerY + containerHeight + _richToolTipMargin
                );
            case "left":
                return new PointInWindow(
                    containerX - tooltipWidth - _richToolTipMargin,
                    containerY + containerHeight / 2 - tooltipHeight / 2
                );
            case "top-left":
                return new PointInWindow(
                    containerX,
                    containerY - tooltipHeight - _richToolTipMargin
                );
            case "top-right":
                return new PointInWindow(
                    containerX + containerWidth - tooltipWidth,
                    containerY - tooltipHeight - _richToolTipMargin
                );
            case "bottom-left":
                return new PointInWindow(
                    containerX,
                    containerY + containerHeight + _richToolTipMargin
                );
            case "bottom-right":
                return new PointInWindow(
                    containerX + containerWidth - tooltipWidth,
                    containerY + containerHeight + _richToolTipMargin
                );
        }
    }

    private checkOverflowAndAdjustIfNeeded(richToolTipLocation: PointInWindow, tooltipWidth: number, tooltipHeight: number): PointInWindow {
        const { _position } = this;
        switch (_position) {
            case "top":
            case "bottom":
            case "top-left":
            case "top-right":
            case "bottom-left":
            case "bottom-right":
                //Top overflow -> move to bottom
                if (richToolTipLocation.y < 0) {
                    console.debug(`📌 adjusting a rich tooltip with top overflow, wanted: ${_position}`);
                    if (_position === "top-left") {
                        richToolTipLocation = this.calculatePointInWindowBasedOnPosition("bottom-left", tooltipWidth, tooltipHeight);
                    } else if (_position === "top-right") {
                        richToolTipLocation = this.calculatePointInWindowBasedOnPosition("bottom-right", tooltipWidth, tooltipHeight);
                    } else {
                        richToolTipLocation = this.calculatePointInWindowBasedOnPosition("bottom", tooltipWidth, tooltipHeight);
                    }
                }
                //Bottom overflow -> move to top
                if (richToolTipLocation.y + tooltipHeight > this.monitorWindowHeight) {
                    console.debug(`📌 adjusting a rich tooltip with bottom overflow, wanted: ${_position}`);
                    if (_position === "bottom-left") {
                        richToolTipLocation = this.calculatePointInWindowBasedOnPosition("top-left", tooltipWidth, tooltipHeight);
                    } else if (_position === "bottom-right") {
                        richToolTipLocation = this.calculatePointInWindowBasedOnPosition("top-right", tooltipWidth, tooltipHeight);
                    } else {
                        richToolTipLocation = this.calculatePointInWindowBasedOnPosition("bottom", tooltipWidth, tooltipHeight);
                    }
                    richToolTipLocation = this.calculatePointInWindowBasedOnPosition("top", tooltipWidth, tooltipHeight);
                }
                //Left overflow
                if (richToolTipLocation.x < 0) {
                    console.debug(`📌 adjusting a rich tooltip with left overflow, wanted: ${_position}`);
                    richToolTipLocation = new PointInWindow(0, richToolTipLocation.y);
                }
                //Right overflow
                if (richToolTipLocation.x + tooltipWidth > this.monitorWindowWidth) {
                    console.debug(`📌 adjusting a rich tooltip with right overflow, wanted: ${_position}`);
                    richToolTipLocation = new PointInWindow(this.monitorWindowWidth - tooltipWidth, richToolTipLocation.y);
                }
                break;
            case "left":
            case "right":
                //Left overflow -> move to right
                if (richToolTipLocation.x < 0) {
                    console.debug(`📌 adjusting a rich tooltip with left overflow, wanted: ${_position}`);
                    richToolTipLocation = this.calculatePointInWindowBasedOnPosition("right", tooltipWidth, tooltipHeight);
                }
                //Right overflow -> move to left
                if (richToolTipLocation.x + tooltipWidth > this.monitorWindowWidth) {
                    console.debug(`📌 adjusting a rich tooltip with right overflow, wanted: ${_position}`);
                    richToolTipLocation = this.calculatePointInWindowBasedOnPosition("left", tooltipWidth, tooltipHeight);
                }
                //Top overflow
                if (richToolTipLocation.y < 0) {
                    console.debug(`📌 adjusting a rich tooltip with top overflow, wanted: ${_position}`);
                    richToolTipLocation = new PointInWindow(richToolTipLocation.x, 0);
                }
                //Bottom overflow
                if (richToolTipLocation.y + tooltipHeight > this.monitorWindowHeight) {
                    console.debug(`📌 adjusting a rich tooltip with bottom overflow, wanted: ${_position}`);
                    richToolTipLocation = new PointInWindow(richToolTipLocation.x, this.monitorWindowHeight - tooltipHeight);
                }
                break;
        }

        return richToolTipLocation;
    }
}
